import { useEffect, useRef } from "react";

/**
 * Makes an overlay a *pushed* state (#11742, Claude Design ruling
 * 2026-08-30): while `active`, one history entry is pushed, so the
 * browser / hardware back gesture dismisses the overlay and returns to the
 * page beneath instead of leaving it. The nav drawer and the phone's
 * full-screen viewers are descents, not modes.
 *
 * Closing the overlay by any other means (scrim, ✕) consumes the pushed
 * entry, so a later back never replays a dismissal — back keeps meaning
 * "up the hierarchy". The entry carries the same URL, the PREVIOUS entry's
 * state plus a marker key: react-router treats it as the location it is
 * already on, and preserving the rest of the state keeps react-router's
 * own `idx` bookkeeping intact (a bare `{}` push poisons its index math
 * for the session — #11742 review B3). Any other code that rewrites the
 * top entry via `replaceState` must likewise carry
 * `window.history.state` forward, or the marker is lost and the entry
 * leaks (review B2; Layout's URL-param writers do this).
 *
 * `renewKey`: an overlay that steps DOWN one level on back (the entity
 * preview stack) stays `active` after the pop consumed its entry — bump
 * the key on each level change and a fresh entry is pushed for the next
 * level (review N13). A key change while the entry still stands (a
 * DESCENT) reuses the standing entry: consuming-and-repushing there queued
 * a traversal that the freshly-registered listener caught, dismissing the
 * level the user just opened (review round 2, blocker 1).
 *
 * ARBITRATION (#11742 round-5 batch review, B2): several overlays can be
 * armed at once — the phone viewer with its Details pane open is two
 * levels, each with its own entry. One popstate pops ONE entry, so it must
 * dismiss ONE overlay: the module keeps a stack of pushed marker entries
 * (by instance), a single popstate listener attributes each pop to the
 * entry it actually consumed, and only that instance dismisses. A silent
 * cleanup pop (closed via ✕) removes its own entry from the bookkeeping
 * BEFORE popping, so the resulting popstate dismisses nothing — closing
 * Details with the Info button must not take the viewer with it.
 */

const MARKER = "__fpOverlayDismiss";

const hasMarker = (state: unknown): boolean =>
  typeof state === "object" && state !== null && MARKER in state;

/**
 * Whether the page sits on an overlay's history entry. An overlay that
 * unmounts while open steps back off its entry (the cleanup below), and the
 * traversal lands on a later turn: the story harness waits for it before it
 * puts the URL and this module's bookkeeping back (`.storybook/vitest.setup.ts`).
 */
export const dismissTraversalPending = (): boolean =>
  hasMarker(window.history.state);

const markerState = (): Record<string, unknown> => {
  const prior: unknown = window.history.state;
  return {
    ...(typeof prior === "object" && prior !== null ? prior : {}),
    [MARKER]: true,
  };
};

// Module-wide bookkeeping: the pushed marker entries in stack order (each
// slot is the instance id that pushed it), and each armed instance's
// dismissal. Entries of DISARMED instances can linger in the stack when a
// navigation buried them — attribution ignores them, and the skip-through
// below absorbs the buried history entry on the way back.
let nextInstanceId = 1;
const markerEntryStack: number[] = [];
const armedDismissals = new Map<number, () => void>();
// Silent cleanup pops in flight: their popstate must not be attributed to
// the next overlay down (its slot would be the stack top by then).
// Accepted risk (re-review 2026-09-01): if a requested silent pop ever
// fails to deliver a popstate, exactly one future back press is swallowed.
// Chromium delivers one popstate per queued traversal (measured); iOS
// Safari untested. A test double that stubs history.back must dispatch a
// synthetic popstate to keep this balanced (see the Layout nav-close
// story); a reset-on-empty self-heal would race an in-flight cleanup pop.
let pendingSilentPops = 0;

/**
 * Forget every armed overlay and pushed marker. Page-scoped by design: the
 * bookkeeping mirrors the browser's history stack, which nothing owns but
 * the page, so it cannot live in a component or a query (rules/frontend.md,
 * "Module state and effects", item 5). The story harness calls this before
 * every story, because on the shared page a marker one story pushed and
 * never popped would be attributed to the next story's first back press
 * (#12187). The popstate listener stays installed; with the stack empty it
 * dismisses nothing.
 */
export function resetDismissOnBackForPage(): void {
  markerEntryStack.length = 0;
  armedDismissals.clear();
  pendingSilentPops = 0;
}

let centralInstalled = false;
function installCentralListener(): void {
  if (centralInstalled) return;
  centralInstalled = true;
  window.addEventListener("popstate", () => {
    if (pendingSilentPops > 0) {
      // A cleanup consumed its own entry; the slot was already removed.
      pendingSilentPops -= 1;
      return;
    }
    // Attribute the pop to the top of OUR entry bookkeeping. A pop of a
    // router entry (in-app navigation history) mis-pops one leaked slot at
    // worst — leaked slots belong to disarmed instances, so nothing
    // dismisses; the depths stay aligned within one leak either way.
    const popped = markerEntryStack.pop();
    if (popped !== undefined) {
      armedDismissals.get(popped)?.();
    }
    // The one leak the cleanup can't reach: open an overlay (marker entry
    // E1), navigate from inside it (router pushes E2 on top) — E1 is
    // buried and nobody can remove it. When Back later LANDS on a marker
    // entry while no overlay is armed, immediately step through it, so
    // the user never feels the dead press (#11742 review B3).
    if (armedDismissals.size === 0 && hasMarker(window.history.state)) {
      window.history.back();
    }
  });
}

export default function useDismissOnBack(
  active: boolean,
  onDismiss: () => void,
  renewKey: number = 0
): void {
  const onDismissRef = useRef(onDismiss);
  useEffect(() => {
    onDismissRef.current = onDismiss;
  }, [onDismiss]);

  // A stable id per hook instance, for the module bookkeeping above.
  const instanceIdRef = useRef(0);
  if (instanceIdRef.current === 0) {
    instanceIdRef.current = nextInstanceId++;
  }

  // Whether OUR pushed entry currently stands on the stack. Shared by the
  // two effects below: the arming effect pushes and (on deactivation)
  // consumes; the re-arm effect replaces an entry a pop already consumed.
  const entryPresentRef = useRef(false);

  useEffect(() => {
    if (!active) return;
    const id = instanceIdRef.current;
    installCentralListener();
    armedDismissals.set(id, () => {
      entryPresentRef.current = false;
      onDismissRef.current();
    });
    window.history.pushState(markerState(), "");
    markerEntryStack.push(id);
    entryPresentRef.current = true;
    return () => {
      armedDismissals.delete(id);
      // Closed without the back gesture: consume the marker entry — with
      // our slot removed FIRST, so the silent pop's popstate dismisses no
      // other armed overlay. If an in-app navigation closed the overlay,
      // the top of the stack is the new route rather than the marker —
      // leave the stack alone; the skip-through absorbs the buried entry
      // on the way back.
      // indexOf, deliberately: an instance holds at most one slot (the
      // arming effect pushes once; the re-arm effect only when the entry
      // was consumed), and stating it here keeps the invariant honest.
      const slot = markerEntryStack.indexOf(id);
      if (slot !== -1) markerEntryStack.splice(slot, 1);
      if (entryPresentRef.current && hasMarker(window.history.state)) {
        pendingSilentPops += 1;
        window.history.back();
      }
      entryPresentRef.current = false;
    };
  }, [active]);

  // Re-arm after a pop consumed the entry while the overlay stayed active
  // (back stepped one preview level down). When the entry still stands —
  // initial arm, or a descent growing the stack — there is nothing to do:
  // the standing entry serves the new level.
  useEffect(() => {
    if (!active || entryPresentRef.current) return;
    window.history.pushState(markerState(), "");
    markerEntryStack.push(instanceIdRef.current);
    entryPresentRef.current = true;
  }, [active, renewKey]);
}
